import {
  Button,
  ButtonGroup,
  DialogFooter,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  closeCurrentDialog,
} from 'platform/components';
import {Grid, GridItem, Space, VStack} from 'platform/foundation';
import {z} from 'zod';

import {UtmParameters} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

const UTM_PARAMETER_MAX_LENGTH = 255;

const utmParameterSchema = z.string().max(UTM_PARAMETER_MAX_LENGTH).nullable();

const schema = z.object({
  source: utmParameterSchema,
  medium: utmParameterSchema,
  campaign: utmParameterSchema,
  term: utmParameterSchema,
  content: utmParameterSchema,
} satisfies Record<keyof UtmParameters, unknown>);

export interface UtmFormProps extends RequiredTestIdProps {
  parameters: UtmParameters;
  onSubmit: (values: UtmParameters) => Promise<unknown>;
}

export function UtmForm(props: UtmFormProps) {
  const handleSubmit: FormSubmitHandler<UtmParameters> = (values) => props.onSubmit(values);

  return (
    <Form
      experimentalZodSchema={schema}
      onSubmit={handleSubmit}
      defaultValues={props.parameters}
      data-testid={props['data-testid']}
    >
      {(control, formApi) => (
        <VStack spacing={4}>
          <Grid columns={2} spacing={4}>
            <GridItem>
              <FormField
                control={control}
                type="text"
                name="source"
                label={i18n.t('entity.interest.utm.source')}
                data-testid={suffixTestId('utm_source', props)}
              />
              <Space vertical={2} />
              <FormField
                control={control}
                type="text"
                name="campaign"
                label={i18n.t('entity.interest.utm.campaign')}
                data-testid={suffixTestId('utm_campaign', props)}
              />
              <Space vertical={2} />
              <FormField
                control={control}
                type="text"
                name="content"
                label={i18n.t('entity.interest.utm.content')}
                data-testid={suffixTestId('utm_content', props)}
              />
            </GridItem>
            <GridItem>
              <FormField
                control={control}
                type="text"
                name="medium"
                label={i18n.t('entity.interest.utm.medium')}
                data-testid={suffixTestId('utm_medium', props)}
              />
              <Space vertical={2} />
              <FormField
                control={control}
                type="text"
                name="term"
                label={i18n.t('entity.interest.utm.term')}
                data-testid={suffixTestId('utm_term', props)}
              />
            </GridItem>
          </Grid>
          <DialogFooter>
            <ButtonGroup align="right">
              <Button
                variant="secondary"
                onClick={closeCurrentDialog}
                title={i18n.t('general.actions.discard')}
                data-testid={suffixTestId('actions.discard', props)}
              />
              <FormButton
                control={control}
                type="submit"
                isLoading={formApi.formState.isSubmitting}
                variant="primary"
                title={i18n.t('general.actions.saveChanges')}
                data-testid={suffixTestId('actions.submit', props)}
              />
            </ButtonGroup>
          </DialogFooter>
        </VStack>
      )}
    </Form>
  );
}
